import React from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    Drawer,
    LinearProgress,
    Typography,
} from '@mui/material';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { SpotDraft, deleteDraft, setActiveDraftId } from 'store/slices/spotDrafts';
import { changeNewSpot, setSelectedCategories, setSelectedHazards } from 'store/slices/newSpot';

interface DraftsPanelProps {
    open: boolean;
    onClose: () => void;
}

const STEP_ROUTES: Record<number, string> = {
    1: '/add-spot',
    2: '/add-spot/step-2',
    3: '/add-spot/step-3',
    4: '/add-spot/step-4',
};

const STEP_LABELS: Record<number, string> = {
    1: 'Location',
    2: 'Categories',
    3: 'Photos',
    4: 'Privacy',
};

const DraftCard = ({
    draft,
    onContinue,
    onDiscard,
}: {
    draft: SpotDraft;
    onContinue: () => void;
    onDiscard: () => void;
}) => {
    const progress = (draft.step / 4) * 100;

    return (
        <Box
            sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                p: 2,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                backgroundColor: 'background.paper',
            }}
        >
            {/* Left: icon + name + progress */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditLocationAltIcon sx={{ color: 'warning.main', flexShrink: 0 }} fontSize="small" />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                            {draft.name || 'Unnamed spot'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Last edited {new Date(draft.updatedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </Typography>
                    </Box>
                    <Chip
                        label={`Step ${draft.step}`}
                        size="small"
                        sx={{
                            ml: 'auto',
                            flexShrink: 0,
                            backgroundColor: 'warning.main',
                            color: 'warning.contrastText',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 20,
                        }}
                    />
                </Box>

                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        {[1, 2, 3, 4].map((s) => (
                            <Typography
                                key={s}
                                variant="caption"
                                sx={{
                                    color: s <= draft.step ? 'warning.main' : 'text.disabled',
                                    fontWeight: s === draft.step ? 700 : 400,
                                    fontSize: '0.6rem',
                                }}
                            >
                                {STEP_LABELS[s]}
                            </Typography>
                        ))}
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            'height': 4,
                            'borderRadius': 2,
                            'backgroundColor': 'action.disabledBackground',
                            '& .MuiLinearProgress-bar': { backgroundColor: 'warning.main', borderRadius: 2 },
                        }}
                    />
                </Box>
            </Box>

            {/* Right: actions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                <Button
                    size="small"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={onContinue}
                >
                    Continue
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={onDiscard}
                >
                    Discard
                </Button>
            </Box>
        </Box>
    );
};

const DraftsPanel = ({ open, onClose }: DraftsPanelProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const drafts = useAppSelector((state) => state.spotDrafts.drafts);

    const handleContinue = (draft: SpotDraft) => {
        void dispatch(changeNewSpot({
            latitude: draft.latitude,
            longitude: draft.longitude,
            zoom: draft.zoom,
            name: draft.name ?? '',
            description: draft.description ?? '',
            youTubeLink: draft.youTubeLink ?? '',
            privacy: draft.privacy,
            anonymity: draft.anonymity,
        }));
        dispatch(setSelectedCategories(draft.selectedCategories ?? []));
        dispatch(setSelectedHazards(draft.selectedHazards ?? []));
        dispatch(setActiveDraftId(draft.id));
        history.push(STEP_ROUTES[draft.step] ?? '/add-spot');
        onClose();
    };

    const handleDiscard = (id: string) => {
        void dispatch(deleteDraft(id));
    };

    return (
        <Drawer anchor="bottom" open={open} onClose={onClose}>
            {/* Drag handle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
                <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: 'divider' }} />
            </Box>

            <Box sx={{ px: 2, pb: 3, maxHeight: '70vh', overflowY: 'auto' }}>
                <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <EditLocationAltIcon sx={{ color: 'warning.main' }} />
                        <Typography variant="h6" fontWeight={700}>
                        Draft spots
                        </Typography>
                        <Chip
                            label={drafts.length}
                            size="small"
                            sx={{ backgroundColor: 'warning.main', color: 'warning.contrastText', fontWeight: 700, height: 20 }}
                        />
                    </Box>

                    {drafts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                        No drafts yet.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {drafts.map((d) => (
                                <DraftCard
                                    key={d.id}
                                    draft={d}
                                    onContinue={() => handleContinue(d)}
                                    onDiscard={() => handleDiscard(d.id)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default DraftsPanel;
